import { BusinessObjectBase } from './base';
import { organizationSchema } from '@schemas/business-objects/organization';
import type { OrganizationPropertyValue } from './organization-property-value';
import type { Student } from './student';
import type { Term } from './term';
import type { GradeLevel, Id, OrganizationType, State } from '../base-types';

export interface OrganizationData {
  readonly id: Id;
  readonly name: string;
  readonly type?: OrganizationType;
  readonly website?: string;
  readonly fromGrade?: GradeLevel;
  readonly toGrade?: GradeLevel;
  readonly phone?: string;
  readonly streetAddress?: string;
  readonly city?: string;
  readonly state?: State;
  readonly zip?: string;
  readonly county?: string;
  readonly organizationPropertyValues?: OrganizationPropertyValue[];
  readonly students?: Student[];
  readonly terms?: Term[];
}

export interface Organization extends Readonly<OrganizationData> {}
type OrganizationCreateWithChildrenData = Partial<
  Omit<OrganizationData, 'organizationPropertyValues' | 'terms'>
> & {
  organizationPropertyValues?: Partial<OrganizationPropertyValue>[];
  terms?: Partial<Term>[];
};

export class Organization extends BusinessObjectBase {
  static readonly entityName = 'Organization' as const;
  static readonly plural = 'organizations';
  static schema = organizationSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Organization[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Organization(data));
  }

  static async findOne(filter?: any): Promise<Organization | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Organization(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Organization(data);
  }

  static async create(
    data: Omit<OrganizationData, 'id'>,
  ): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Organization(result);
  }

  static async createMany(
    data: Omit<OrganizationData, 'id'>[],
  ): Promise<Organization[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Organization(item));
  }

  static async createWithChildren(
    data: OrganizationCreateWithChildrenData,
  ): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Organization(result);
  }

  static async createManyWithChildren(
    data: OrganizationCreateWithChildrenData[],
  ): Promise<Organization[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Organization(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<OrganizationData>,
  ): Promise<Organization> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Organization(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
